import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle, RefreshCw, Loader2, ChevronUp } from 'lucide-react';
import { formatDate } from '../utils/dateFormatter';
import FindingCrossLinks from './FindingCrossLinks';

const STATUS_BADGE = {
  passed: { className: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Passed', icon: CheckCircle },
  insufficient_evidence: { className: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Needs review', icon: AlertTriangle },
  failed: { className: 'bg-red-100 text-red-800 border-red-200', label: 'Failed', icon: ShieldAlert },
};

const TwoSVEnforcementWidget = ({ workspaceId, gwConnected }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdmins, setShowAdmins] = useState(true);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      const resp = await axios.get(`/api/employees/two-sv-enforcement`, {
        params: { workspace_id: workspaceId },
      });
      setData(resp.data);
    } catch (err) {
      console.error('two-sv-enforcement load failed', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!gwConnected) return null;

  const statusKey = data?.status || 'passed';
  const statusMeta = STATUS_BADGE[statusKey] || STATUS_BADGE.passed;
  const StatusIcon = statusMeta.icon;
  const t = data?.totals || {};

  const nonAdminPct = t.non_admin_enrollment_pct || 0;
  const passPct = t.pass_threshold_percent || 95;

  return (
    <Card className="border-slate-200 mb-6" data-testid="two-sv-widget">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-slate-500" strokeWidth={1.7} />
              2-Step Verification Enforcement
              {data && (
                <Badge className={`${statusMeta.className} inline-flex items-center gap-1`}>
                  <StatusIcon className="w-3 h-3" />
                  {statusMeta.label}
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              SOC 2 CC6.1 · ISO 27001 A.5.16
            </p>
          </div>
          <Button
            onClick={load}
            variant="ghost"
            size="sm"
            disabled={loading}
            data-testid="two-sv-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {loading && !data && (
          <div className="py-6 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Evaluating directory…
          </div>
        )}

        {data && (
          <>
            <p className="text-sm text-slate-700 mb-4" data-testid="two-sv-proof">
              {data.proof}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div
                className={`border rounded-lg p-3 ${
                  t.admins_unenrolled > 0
                    ? 'bg-red-50 border-red-200 text-red-900'
                    : 'bg-emerald-50 border-emerald-200 text-emerald-900'
                }`}
                data-testid="two-sv-admin-tile"
              >
                <div className="flex items-center justify-between mb-1">
                  <ShieldAlert className="w-4 h-4" strokeWidth={1.7} />
                  <span className="text-2xl font-semibold leading-none">
                    {t.admins_enrolled || 0}/{t.admins_total || 0}
                  </span>
                </div>
                <div className="text-xs font-medium">Admin enrolment</div>
              </div>

              <div
                className={`border rounded-lg p-3 ${
                  nonAdminPct >= passPct
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    : nonAdminPct >= 85
                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                    : 'bg-red-50 border-red-200 text-red-900'
                }`}
                data-testid="two-sv-nonadmin-tile"
              >
                <div className="flex items-center justify-between mb-1">
                  <ShieldCheck className="w-4 h-4" strokeWidth={1.7} />
                  <span className="text-2xl font-semibold leading-none">
                    {nonAdminPct}%
                  </span>
                </div>
                <div className="text-xs font-medium">
                  Non-admin enrolment · target {passPct}%
                </div>
              </div>

              <div
                className="border rounded-lg p-3 bg-slate-50 border-slate-200 text-slate-700"
                data-testid="two-sv-total-tile"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-2xl font-semibold leading-none">
                    {t.non_admins_enrolled || 0}/{t.non_admins_total || 0}
                  </span>
                </div>
                <div className="text-xs font-medium">Non-admin users</div>
              </div>

              <div
                className={`border rounded-lg p-3 ${
                  t.org_wide_enforcement_on
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                }`}
                data-testid="two-sv-enforcement-tile"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold leading-none">
                    {t.org_wide_enforcement_on ? 'On' : 'Off / Mixed'}
                  </span>
                </div>
                <div className="text-xs font-medium">Org-wide enforcement</div>
              </div>
            </div>

            {(data.unenrolled_admins || []).length > 0 && (
              <div className="border rounded-lg border-red-200 bg-red-50/40 mb-3" data-testid="two-sv-admin-drilldown">
                <button
                  type="button"
                  onClick={() => setShowAdmins(s => !s)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-red-900 hover:bg-red-50"
                >
                  <span>
                    {data.unenrolled_admins.length} admin(s) without 2SV — critical
                  </span>
                  <ChevronUp className={`w-3 h-3 transition-transform ${showAdmins ? '' : 'rotate-180'}`} />
                </button>
                {showAdmins && (
                  <div className="divide-y divide-red-100 bg-white rounded-b-lg">
                    {data.unenrolled_admins.map((u) => (
                      <div key={u.source_user_id} className="px-3 py-2 text-sm">
                        <div className="font-medium text-slate-900">{u.full_name || u.primary_email}</div>
                        <div className="text-xs text-slate-500">{u.primary_email}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {data.what_would_fix && (
              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3">
                <span className="font-medium text-slate-700">Next steps:</span> {data.what_would_fix}
              </div>
            )}

            <FindingCrossLinks
              ruleKey="directory.access.two_sv_enforcement"
              matches={data.unenrolled_admins || []}
              workspaceId={workspaceId}
            />

            <div className="mt-3 text-[11px] text-slate-400">
              Evaluated {formatDate(data.evaluated_at)} · {t.active_users || 0} active users
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TwoSVEnforcementWidget;
